/**
 * Authentication Context
 * ======================
 * Global authentication state management
 */

import React, { createContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../api';

// Create context
export const AuthContext = createContext(null);

/**
 * Authentication Provider Component
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Load current user on mount
   */
  useEffect(() => {
    const loadUser = async () => {
      if (authAPI.isAuthenticated()) {
        try {
          const userData = await authAPI.getCurrentUser();
          setUser(userData);
        } catch (err) {
          console.error('Failed to load user:', err);
          // Token might be invalid, clear it
          authAPI.logout();
        }
      }
      setLoading(false);
    };

    loadUser();
  }, []);

  /**
   * Login function
   */
  const login = useCallback(async (email, password, orgSlug, captchaToken) => {
    setLoading(true);
    setError(null);

    try {
      const response = await authAPI.login(email, password, orgSlug, captchaToken);
      // Merge organizations from the top-level login response into the user object
      // so OrgContext can read user.organizations immediately without waiting for /me
      setUser({ ...response.user, organizations: response.organizations || [] });
      return response;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Logout function
   */
  const logout = useCallback(async () => {
    setLoading(true);
    try {
      await authAPI.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      localStorage.removeItem('zamanat_active_org_id');
      setLoading(false);
    }
  }, []);

  /**
   * Refresh user data
   */
  const refreshUser = useCallback(async () => {
    if (!authAPI.isAuthenticated()) {
      setUser(null);
      return;
    }

    try {
      const userData = await authAPI.getCurrentUser();
      setUser(userData);
    } catch (err) {
      console.error('Failed to refresh user:', err);
      setUser(null);
    }
  }, []);

  /**
   * Update user data directly (without API call)
   */
  const updateUser = useCallback((userData) => {
    setUser(userData);
  }, []);

  /**
   * Change password
   */
  const changePassword = useCallback(async (currentPassword, newPassword, confirmPassword) => {
    setError(null);

    try {
      const response = await authAPI.changePassword(
        currentPassword,
        newPassword,
        confirmPassword
      );

      // If must login again, clear user state
      if (response.must_login_again) {
        setUser(null);
      }

      return response;
    } catch (err) {
      setError(err);
      throw err;
    }
  }, []);

  /**
   * Check if user has permission
   */
  const hasPermission = useCallback(
    (permission) => {
      if (!user || !user.permissions) return false;

      // Admin wildcard check
      if (user.permissions.includes('*')) return true;

      // Direct permission check
      return user.permissions.includes(permission);
    },
    [user]
  );

  /**
   * Check if user has any of the permissions
   */
  const hasAnyPermission = useCallback(
    (permissions) => {
      if (!user || !user.permissions) return false;

      // Admin wildcard check
      if (user.permissions.includes('*')) return true;

      // Check if user has any of the required permissions
      return permissions.some((perm) => user.permissions.includes(perm));
    },
    [user]
  );

  /**
   * Check if user has all permissions
   */
  const hasAllPermissions = useCallback(
    (permissions) => {
      if (!user || !user.permissions) return false;

      // Admin wildcard check
      if (user.permissions.includes('*')) return true;

      // Check if user has all required permissions
      return permissions.every((perm) => user.permissions.includes(perm));
    },
    [user]
  );

  /**
   * Check if user has role
   */
  const hasRole = useCallback(
    (role) => {
      if (!user || !user.roles) return false;
      return user.roles.includes(role);
    },
    [user]
  );

  /**
   * Check if user has any of the roles
   */
  const hasAnyRole = useCallback(
    (roles) => {
      if (!user || !user.roles) return false;
      return roles.some((role) => user.roles.includes(role));
    },
    [user]
  );

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    logout,
    refreshUser,
    updateUser,
    changePassword,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasAnyRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

